var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton } from '@claims-ia/design-system';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import FileCollectionUpload from '@declaration/components/upload/FileCollectionUpload.vue';
import AmountInput from '@declaration/components/UI/AmountInput.vue';
import RadioPrimaryButton from '@declaration/components/UI/RadioPrimaryButton.vue';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import { GLASS_BREAKAGE_SECURITY_REPARATION_BY_CHOICES } from '@shared/constants/glassBreakageDamageConstants';
import { GLASS_BREAKAGE_SECURITY_REPARATION_TYPE_CHOICES, GLASS_BREAKAGE_SECURITY_REPARATION_TYPE_ANSWERS, GLASS_BREAKAGE_SECURITY_REPARATION_BY_ANSWERS, GLASS_BREAKAGE_TRAD_KEYS, } from '@declaration/constants/glassBreakageDamageConstants';
import { GlassBreakageSecurityReparationBy, GlassBreakageSecurityReparationType, } from '@shared/types/api/glassBreakageDamage';
import { ClaimFileType } from '@shared/types/file';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
import { ChargeTypology } from '@shared/types/api/charge';
const claim = namespace('claim');
const qualification = namespace('qualification');
let GlassBreakageSecurityQuestion = class GlassBreakageSecurityQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.invoiceFileCollection = null;
        this.temporaryReparationsInvoiceAmount = null;
        this.revealValidationViolations = false;
    }
    created() {
        this.invoiceFileCollection = null;
        this.temporaryReparationsInvoiceAmount = null;
    }
    get panelTitle() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['security-reparation-type-panel-title']);
    }
    get sidebarTitle() {
        const key = GLASS_BREAKAGE_TRAD_KEYS['security-reparation-type-question-sidebar_title'];
        return key && this.$te(key) ? this.$tc(key) : this.panelTitle;
    }
    get reparationTypeChoices() {
        return GLASS_BREAKAGE_SECURITY_REPARATION_TYPE_CHOICES;
    }
    get reparationByChoices() {
        return GLASS_BREAKAGE_SECURITY_REPARATION_BY_CHOICES;
    }
    get selectedReparationTypeValue() {
        return this.qualification.temporaryReparationsType;
    }
    get selectedReparationByValue() {
        return this.qualification.temporaryReparationsBy;
    }
    get shouldDisplayReparationBy() {
        return (this.selectedReparationTypeValue !== null &&
            this.selectedReparationTypeValue !== undefined &&
            this.selectedReparationTypeValue !== GlassBreakageSecurityReparationType.None);
    }
    get shouldDisplayReparationInvoice() {
        return (this.selectedReparationTypeValue !== null &&
            this.selectedReparationTypeValue !== undefined &&
            this.selectedReparationTypeValue !== GlassBreakageSecurityReparationType.None &&
            this.selectedReparationByValue !== null &&
            this.selectedReparationByValue !== undefined &&
            this.selectedReparationByValue !== GlassBreakageSecurityReparationBy.Assistance);
    }
    get shouldDisplayReparationInvoiceAmount() {
        var _a;
        switch (this.selectedReparationByValue) {
            case GlassBreakageSecurityReparationBy.Professional:
                return true;
            case GlassBreakageSecurityReparationBy.Myself:
                return !!((_a = this.invoiceFileCollection) === null || _a === void 0 ? void 0 : _a.uploadedAppFiles.length);
            default:
                return false;
        }
    }
    get isReparationInvoiceMandatory() {
        return this.selectedReparationByValue === GlassBreakageSecurityReparationBy.Professional;
    }
    get selectedTemporaryReparationsInvoiceAmount() {
        return this.temporaryReparationsInvoiceAmount;
    }
    get canValidateInvoice() {
        var _a;
        const hasFiles = !!((_a = this.invoiceFileCollection) === null || _a === void 0 ? void 0 : _a.uploadedAppFiles.length);
        const isAmountFilled = !!this.temporaryReparationsInvoiceAmount;
        const mustValidateFiles = !this.hasDisableDamagesJustificatives;
        if (mustValidateFiles) {
            if (this.isReparationInvoiceMandatory && !hasFiles)
                return false;
            if (hasFiles && !isAmountFilled)
                return false;
        }
        if (this.hasDisableDamagesJustificatives &&
            this.shouldDisplayReparationInvoiceAmount &&
            !isAmountFilled)
            return false;
        return true;
    }
    get invoiceFileType() {
        return ClaimFileType.CraftsmanInvoice;
    }
    get answer() {
        if (this.selectedReparationTypeValue === null)
            return null;
        if (this.selectedReparationTypeValue === GlassBreakageSecurityReparationType.None ||
            this.selectedReparationByValue === null) {
            return this.$tc(GLASS_BREAKAGE_SECURITY_REPARATION_TYPE_ANSWERS[this.selectedReparationTypeValue]);
        }
        return `${this.$tc(GLASS_BREAKAGE_SECURITY_REPARATION_TYPE_ANSWERS[this.selectedReparationTypeValue])}&nbsp;${this.$tc(GLASS_BREAKAGE_SECURITY_REPARATION_BY_ANSWERS[this.selectedReparationByValue])}`;
    }
    get reparationByQuestionTitle() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['security-reparation-by-question']);
    }
    get reparationInvoiceQuestionTitle() {
        return this.$tc(this.isReparationInvoiceMandatory
            ? GLASS_BREAKAGE_TRAD_KEYS['security-reparation-mandatory-invoice-question']
            : GLASS_BREAKAGE_TRAD_KEYS['security-reparation-optional-invoice-question']);
    }
    get reparationInvoiceQuestionDescription() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['security-reparation-invoice-question-description']);
    }
    get reparationAddInvoiceLabel() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['security-reparation-invoice-question-upload']);
    }
    get hasDisableDamagesJustificatives() {
        return insurerHasFeature(InsurerFeatureFlag.DisableDamagesJustificatives);
    }
    get invoiceValidation() {
        var _a;
        if (this.isReparationInvoiceMandatory && !((_a = this.invoiceFileCollection) === null || _a === void 0 ? void 0 : _a.uploadedAppFiles.length)) {
            return {
                isValid: false,
                error: this.$tc('validator.requiredFileCollection'),
            };
        }
        return { isValid: true, error: null };
    }
    get amountValidation() {
        if (this.shouldDisplayReparationInvoiceAmount && !this.temporaryReparationsInvoiceAmount) {
            return {
                isValid: false,
                error: this.$tc('claim.damage.glass_breakage.question.security.reparation_invoice.validator.presence'),
            };
        }
        return { isValid: true, error: null };
    }
    get validation() {
        return {
            fieldIsValid: (field) => {
                if (!this.revealValidationViolations)
                    return true;
                switch (field) {
                    case 'temporaryReparationsInvoice':
                        return this.invoiceValidation.isValid;
                    case 'temporaryReparationsInvoiceAmount':
                        return this.amountValidation.isValid;
                    default:
                        return true;
                }
            },
            fieldErrors: (field) => {
                if (!this.revealValidationViolations)
                    return null;
                switch (field) {
                    case 'temporaryReparationsInvoice':
                        return this.invoiceValidation.error;
                    case 'temporaryReparationsInvoiceAmount':
                        return this.amountValidation.error;
                    default:
                        return null;
                }
            },
            revealViolations: () => {
                this.revealValidationViolations = true;
            },
        };
    }
    get selectedTemporaryReparationsInvoice() {
        return this.invoiceFileCollection;
    }
    onReparationTypeUpdate(type) {
        if (type === GlassBreakageSecurityReparationType.None) {
            this.updateAndSaveGlassBreakageDamageQualification({
                step: this.step,
                qualificationPayload: { temporaryReparationsType: type, temporaryReparationsBy: null },
            });
        }
        else {
            this.updateGlassBreakageDamageQualification({
                qualificationPayload: { temporaryReparationsType: type },
            });
        }
    }
    onReparationByUpdate(by) {
        if (by === GlassBreakageSecurityReparationBy.Assistance) {
            this.updateAndSaveGlassBreakageDamageQualification({
                step: this.step,
                qualificationPayload: {
                    temporaryReparationsBy: by,
                    temporaryReparationsType: this.selectedReparationTypeValue,
                },
            });
        }
        else {
            this.updateGlassBreakageDamageQualification({
                qualificationPayload: { temporaryReparationsBy: by },
            });
        }
    }
    onSafeFileUpdate(files) {
        if (!files || files.length === 0)
            return;
        this.onFileCollectionUpdate(files);
    }
    onFileCollectionUpdate(fileCollection) {
        this.invoiceFileCollection = fileCollection;
    }
    onTemporaryReparationsInvoiceAmountUpdate(amount) {
        this.temporaryReparationsInvoiceAmount = amount;
    }
    async onValidate() {
        if (!this.canValidateInvoice) {
            this.validation.revealViolations();
            return;
        }
        const isProfessional = this.selectedReparationByValue === GlassBreakageSecurityReparationBy.Professional;
        const hasInvoice = !!this.invoiceFileCollection;
        const hasAmount = this.temporaryReparationsInvoiceAmount !== null;
        if (isProfessional && (!hasInvoice || !hasAmount)) {
            this.validation.revealViolations();
            return;
        }
        const charges = [];
        if (hasInvoice && hasAmount) {
            charges.push({
                typology: ChargeTypology.SecuringFees,
                amount: this.temporaryReparationsInvoiceAmount,
                invoice: this.invoiceFileCollection,
                description: null,
            });
        }
        await this.updateAndSaveGlassBreakageDamageQualification({
            step: this.step,
            qualificationPayload: {
                temporaryReparationsType: this.selectedReparationTypeValue,
                temporaryReparationsBy: this.selectedReparationByValue,
            },
        });
        await this.updateAndSaveClaim({
            step: this.step,
            claimPayload: {},
            charges,
        });
    }
};
__decorate([
    claim.State('claim')
], GlassBreakageSecurityQuestion.prototype, "claim", void 0);
__decorate([
    claim.State('isUpdatingClaim')
], GlassBreakageSecurityQuestion.prototype, "isUpdatingClaim", void 0);
__decorate([
    claim.Action('updateAndSaveClaim')
], GlassBreakageSecurityQuestion.prototype, "updateAndSaveClaim", void 0);
__decorate([
    qualification.State('qualification')
], GlassBreakageSecurityQuestion.prototype, "qualification", void 0);
__decorate([
    qualification.Action('updateQualification')
], GlassBreakageSecurityQuestion.prototype, "updateGlassBreakageDamageQualification", void 0);
__decorate([
    qualification.Action('updateAndSaveQualification')
], GlassBreakageSecurityQuestion.prototype, "updateAndSaveGlassBreakageDamageQualification", void 0);
GlassBreakageSecurityQuestion = __decorate([
    Component({
        components: {
            RadioPrimaryButton,
            QuestionPanel,
            UiButton,
            FileCollectionUpload,
            AmountInput,
        },
        props: QuestionProps,
    })
], GlassBreakageSecurityQuestion);
export default GlassBreakageSecurityQuestion;
